import React, { useState, useEffect, useMemo } from "react";
import PaginatedSortableControls from "../components/PaginatedSortableControls";
import axios from "axios";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import PaginatedSortableTable from "../components/PaginatedSortableTable";
import AgentTree from "../components/AgentTree";
import $ from "jquery";
import "jstree";
import "jstree/dist/themes/default/style.css";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import MakeAgentModal from "../components/MakeAgentModal";
import AmountModal from "../components/AmountModal";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function Agents() {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const { setIsLoggedIn } = useOutletContext();
  const navigate = useNavigate();
  const [users, setUser] = useState(null);
  const [agents, setAgents] = useState(null);
  const [agents_org, setAgents_org] = useState(null);
  const [showPendingOnly, setShowPendingOnly] = useState(false);
  const [selectAgent, setSelectAgent] = useState(null);
  const [topAgent, setTopAgent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAModal, setShowAModal] = useState(false);
  const [modalType, setModalType] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({
    field: "cash",
    order: "desc",
  });

  const displayedAgents = useMemo(() => {
    return (agents || [])
      .filter((a) => (showPendingOnly ? a.status === "PENDING" : true))
      .filter((a) =>
        searchText.trim() !== ""
          ? a.user_id.toLowerCase().includes(searchText.toLowerCase())
          : true,
      );
  }, [agents, showPendingOnly, searchText]);

  const sortedAgents = useMemo(() => {
    const list = [...displayedAgents];

    list.sort((a, b) => {
      const getValue = (agent) => {
        switch (sortConfig.field) {
          case "cash":
            return (agent.possess_amt ?? 0) + (agent.subPossessAmt ?? 0);
          case "reg_dt":
            return new Date(agent.reg_dt).getTime();
          default:
            return agent?.[sortConfig.field] ?? 0;
        }
      };

      const aVal = getValue(a);
      const bVal = getValue(b);

      if (aVal < bVal) return sortConfig.order === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.order === "asc" ? 1 : -1;
      return 0;
    });

    return list;
  }, [displayedAgents, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedAgents.length / rowsPerPage));

  const pagedAgents = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return sortedAgents.slice(start, start + rowsPerPage);
  }, [sortedAgents, currentPage, rowsPerPage]);

  const formatDateWithTime = (datetimeStr) => {
    if (!datetimeStr) return "-";
    const date = dayjs.utc(datetimeStr).tz("Asia/Seoul");
    return `${date.format("YYYY-MM-DD")} (${date.format("HH:mm:ss")} UTC +9)`;
  };

  const handlePendingToggle = (e) => {
    setShowPendingOnly(e.target.checked);
  };

  const openModal = (agent, baseType) => {
    let finalType = 1; // 기본: 정보수정 (화이트리스트/콜백 X)

    finalType = baseType === 0 ? 2 : 3;
    if (users.myId === "root") {
      // root만 고급 타입 허용
      finalType = baseType === 0 ? 2 : 3;
      // 0 → 생성 → 2
      // 1 → 수정 → 3
    }

    setSelectAgent(agent);
    setModalType(finalType);
    setShowModal(true);
  };

  const openModal2 = (agent, type) => {
    setSelectAgent(agent);
    setShowAModal(true);
    setModalType(type);
  };

  const closeModal2 = () => {
    setShowAModal(false);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const move = (user) => {
    navigate("/agentInfo", {
      state: {
        user: user,
      },
    });
  };

  const sortOptions = [
    { value: "cash:asc", label: "총보유알 - 오름차순" },
    { value: "cash:desc", label: "총보유알 - 내림차순" },
    { value: "reg_dt:asc", label: "생성일 - 오름차순" },
    { value: "reg_dt:desc", label: "생성일 - 내림차순" },
  ];

  const handleSelectAgent = (prefix) => {
    if (!agents || !prefix) return;
    const matched = agents.find((a) => a.prefix === prefix);
    if (matched) {
      setSelectAgent(matched);
      fetchAgentInfo(matched.user_id);
    } else {
      console.warn("prefix에 해당하는 agent를 찾을 수 없습니다:", prefix);
    }
  };

  const fetchAgentInfo = async (agentId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE}/api/Agents`, {
        params: { agentId },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setAgents(res.data.agents);
      setSelectAgent(res.data.agents[0]);
      setTopAgent(res.data.agents[0]);
      if (agents_org !== null) {
        return;
      }
      setAgents_org(res.data.agents);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        if (
          err.response.status === 401 ||
          err.response.status === 403 ||
          err.response.status === 500
        ) {
          localStorage.removeItem("token");
          setIsLoggedIn(false); // 로그인 상태 false 처리
          navigate("/login"); // 로그인 화면으로 리다이렉트
          return;
        }
      }
      console.error("유저 정보 불러오기 실패:", err);
    }
  };

  const renderUpperBadges = (agent, allAgents) => {
    const badges = [];
    if (agent.up_user_id_list) {
      const upIds = agent.up_user_id_list.split(",");

      upIds.forEach((uid, index) => {
        const upperAgent = allAgents.find((a) => a.user_id === uid);
        if (!upperAgent) return;

        const badgeClass =
          upperAgent.auth_level === "0" ? "super" : `M${upperAgent.auth_level}`;
        const label = upperAgent.user_id;

        badges.push(
          <em
            key={`upper-${index}`}
            className={`badge me-1 ${badgeClass}`}
            style={{ cursor: "pointer" }}
          >
            {label}
          </em>,
        );

        // 4개마다 줄바꿈
        if ((index + 1) % 4 === 0) {
          badges.push(<br key={`br-${index}`} />);
        }
      });
    }

    // 본인 뱃지
    const selfClass =
      agent.auth_level === "0" ? "super" : `M${agent.auth_level}`;
    badges.push(
      <em
        key="self"
        className={`badge me-1 ${selfClass}`}
        style={{ cursor: "pointer" }}
      >
        {agent.user_id}
      </em>,
    );

    return <>{badges}</>;
  };

  useEffect(() => {
    if (!users || agents !== null) {
      return;
    }
    fetchAgentInfo(users.myId);
  }, [users]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_BASE}/api/Users`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setUser(res.data);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response) {
          if (
            err.response.status === 401 ||
            err.response.status === 403 ||
            err.response.status === 500
          ) {
            localStorage.removeItem("token");
            setIsLoggedIn(false); // 로그인 상태 false 처리
            navigate("/login"); // 로그인 화면으로 리다이렉트
            return;
          }
        }
        console.error("유저 정보 불러오기 실패:", err);
      }
    };

    fetchUserInfo();
  }, []);

  const renderRow = (agent, index) => {
    const cash = agent.possess_amt + agent.subPossessAmt;
    if (agent.user_id === "root") return null;
    return (
      <tr data-pos-y="0" key={index}>
        <td
          colSpan="1"
          data-columns="id5fa3e4edf1ca8-col-0"
          data-org-colspan="1"
          data-pos-x="0"
          data-priority="1"
          style={{
            display: "table-cell",
          }}
        >
          <span>{renderUpperBadges(agent, agents_org)}</span>
        </td>
        <td
          colSpan="1"
          data-columns="id5fa3e4edf1ca8-col-1"
          data-org-colspan="1"
          data-pos-x="1"
          data-priority="1"
          style={{
            display: "table-cell",
          }}
        >
          <div className="user-toggle">
            <a className="dropdown-toggle" data-bs-toggle="dropdown" href="#">
              <div className="user-toggle">
                <div className="user-info">
                  <div className="user-name dropdown-indicator">
                    <span className="badge rounded-pill badge-dim bg-outline-danger me-1">
                      아이디
                    </span>
                    {agent.user_id}
                  </div>
                  <div className="user-status">
                    <span className="badge rounded-pill badge-dim bg-outline-primary me-1">
                      닉네임
                    </span>
                    {agent.user_id}
                  </div>
                </div>
              </div>
            </a>
            <div
              className="dropdown-menu dropdown-menu-start"
              style={{
                position: "relative",
                zIndex: "999",
              }}
            >
              <ul className="link-check">
                <li>
                  <div className="p-1">
                    <div className="user-name">
                      <span className="badge rounded-pill badge-dim bg-outline-info me-1">
                        지갑
                      </span>
                      <em className="badge rounded-pill badge-dim bg-outline-info me-1 simm">
                        S
                      </em>
                    </div>
                    <div className="user-name">
                      <span className="badge rounded-pill badge-dim bg-outline-danger me-1">
                        아이디
                      </span>
                      {agent.user_id}
                    </div>
                    <div className="user-name">
                      <span className="badge rounded-pill badge-dim bg-outline-primary me-1">
                        닉네임
                      </span>
                      {agent.user_id}
                    </div>
                  </div>
                </li>
                <li className="divider" />
                <li>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigator.clipboard
                        .writeText(agent.user_id)
                        .then(() => {})
                        .catch((err) => {
                          console.error("복사 실패:", err);
                        });
                    }}
                  >
                    <em className="icon far fa-copy" />
                    <span>아이디 복사</span>
                  </a>
                </li>
                {(agent.auth_level > Math.max(users.myLevel, 0) ||
                  users.myId === "root") && (
                  <>
                    <li className="divider" />
                    <li>
                      <a href="#" onClick={() => openModal2(agent, 0)}>
                        <em className="icon ni ni-tranx"></em>
                        <span>지급</span>
                      </a>
                    </li>
                    <li>
                      <a href="#" onClick={() => openModal2(agent, 1)}>
                        <em className="icon ni ni-tranx"></em>
                        <span>회수</span>
                      </a>
                    </li>
                  </>
                )}

                {/* <li className="divider" />
                <li>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault(); // 링크 이동 기본 동작 취소
                      // move(agent); // React Router navigate 호출
                    }}
                  >
                    <em className="icon ni ni-info" />
                    <span>에이전트 정보</span>
                  </a>
                </li> */}
                <li className="divider" />
                <li>
                  <a href="#" onClick={() => openModal(agent, 0)}>
                    <em className="icon ni ni-plus-c" />
                    <span>하부생성</span>
                  </a>
                </li>
                <li className="divider" />
                <li>
                  <a href="#" onClick={() => openModal(agent, 1)}>
                    <em className="icon ni ni-edit" />
                    <span>정보수정</span>
                  </a>
                </li>
                <li className="divider" />
              </ul>
            </div>
          </div>
        </td>
        <td
          className="text-center rowspan"
          colSpan="1"
          data-columns="id5fa3e4edf1ca8-col-2"
          data-org-colspan="1"
          data-pos-x="2"
          data-priority="1"
          style={{
            display: "table-cell",
          }}
        >
          선불
        </td>
        <td
          className="text-center rowspan"
          colSpan="1"
          data-columns="id5fa3e4edf1ca8-col-3"
          data-org-colspan="1"
          data-pos-x="3"
          data-priority="1"
          style={{
            display: "table-cell",
          }}
        >
          <em className="badge rounded-pill badge-dim bg-outline-info me-1 simm">
            S
          </em>
        </td>
        <td
          className="text-center rowspan"
          colSpan="1"
          data-columns="id5fa3e4edf1ca8-col-4"
          data-org-colspan="1"
          data-pos-x="4"
          data-priority="1"
          style={{
            display: "table-cell",
          }}
        >
          {agent.rate}%
        </td>
        <td
          className="text-center"
          colSpan="1"
          data-columns="id5fa3e4edf1ca8-col-5"
          data-org-colspan="1"
          data-pos-x="5"
          data-priority="1"
          style={{
            display: "table-cell",
          }}
        >
          KRW
        </td>
        <td
          className="text-end"
          colSpan="1"
          data-columns="id5fa3e4edf1ca8-col-6"
          data-org-colspan="1"
          data-pos-x="6"
          data-priority="1"
          style={{
            display: "table-cell",
          }}
        >
          <span className="badge rounded-pill badge-dim bg-outline-danger me-1">
            일반
          </span>
          <strong>
            <span>{agent.possess_amt.toLocaleString()}</span>
          </strong>
        </td>
        <td
          className="text-end rowspan"
          colSpan="1"
          data-columns="id5fa3e4edf1ca8-col-7"
          data-org-colspan="1"
          data-pos-x="7"
          data-priority="1"
          style={{
            display: "table-cell",
          }}
        >
          <span>{agent.subPossessAmt.toLocaleString()}</span>
          <hr />
          <span>0</span>
        </td>
        <td
          className="text-end"
          colSpan="1"
          data-columns="id5fa3e4edf1ca8-col-8"
          data-org-colspan="1"
          data-pos-x="8"
          data-priority="1"
          style={{
            display: "table-cell",
          }}
        >
          <strong>
            <span>{cash.toLocaleString()}</span>
          </strong>
          <hr />
          <strong>
            <span>0</span>
          </strong>
        </td>
        <td
          className="text-end"
          colSpan="1"
          data-columns="id5fa3e4edf1ca8-col-9"
          data-org-colspan="1"
          data-pos-x="9"
          data-priority="1"
          style={{
            display: "table-cell",
          }}
        >
          <div className="paid-back">
            <span className="paid clearfix">
              <em className="icon ni ni-plus-round-fill" />
              <span>{agent.toType0.toLocaleString()}</span>
            </span>
            <span className="pback">
              <em className="icon ni ni-minus-round-fill" />
              <span>{agent.toType1.toLocaleString()}</span>
            </span>
            <hr />
            <span className="total">
              <em className="icon ni ni-arrow-right-fill-c" />
              <span>{(agent.toType0 - agent.toType1).toLocaleString()}</span>
            </span>
          </div>
        </td>
        <td
          className="text-end"
          colSpan="1"
          data-columns="id5fa3e4edf1ca8-col-10"
          data-org-colspan="1"
          data-pos-x="10"
          data-priority="1"
          style={{
            display: "table-cell",
          }}
        >
          <div className="paid-back">
            <span className="paid clearfix">
              <em className="icon ni ni-plus-round-fill" />
              <span>{agent.fromType0.toLocaleString()}</span>
            </span>
            <span className="pback">
              <em className="icon ni ni-minus-round-fill" />
              <span>{agent.fromType1.toLocaleString()}</span>
            </span>
            <hr />
            <span className="total">
              <em className="icon ni ni-arrow-right-fill-c" />
              <span>
                {(agent.fromType0 - agent.fromType1).toLocaleString()}
              </span>
            </span>
          </div>
        </td>
        <td
          className="text-end"
          colSpan="1"
          data-columns="id5fa3e4edf1ca8-col-11"
          data-org-colspan="1"
          data-pos-x="11"
          data-priority="1"
          style={{
            display: "table-cell",
          }}
        >
          <div className="paid-back">
            <span className="paid clearfix">
              <em className="icon ni ni-plus-round-fill" />
              <span>0</span>
            </span>
            <span className="pback">
              <em className="icon ni ni-minus-round-fill" />
              <span>0</span>
            </span>
            <hr />
            <span className="total">
              <em className="icon ni ni-arrow-right-fill-c" />
              <span>0</span>
            </span>
          </div>
        </td>
        <td
          className="text-center"
          colSpan="1"
          data-columns="id5fa3e4edf1ca8-col-12"
          data-org-colspan="1"
          data-pos-x="12"
          data-priority="1"
          style={{
            display: "table-cell",
          }}
        >
          {/* 가입일 */}
          <div style={{ display: "table-row" }}>
            <div style={{ display: "table-cell" }}>
              <span className="small badge badge-dot bg-secondary badge-date me-1">
                가입일
              </span>
            </div>
            <div style={{ display: "table-cell" }}>
              <span style={{ fontSize: "95%" }}>
                {formatDateWithTime(agent.reg_dt)}
              </span>
            </div>
          </div>

          <hr className="m-0" />

          {/* 수정일 */}
          <div style={{ display: "table-row" }}>
            <div style={{ display: "table-cell" }}>
              <span className="small badge badge-dot bg-primary badge-date me-1">
                수정일
              </span>
            </div>
            <div style={{ display: "table-cell" }}>
              <span style={{ fontSize: "95%" }}>
                {formatDateWithTime(agent.mod_dt)}
              </span>
            </div>
          </div>

          <hr className="m-0" />

          {/* 접속일 */}
          <div style={{ display: "table-row" }}>
            <div style={{ display: "table-cell" }}>
              <span className="small badge badge-dot bg-danger badge-date me-1">
                접속일
              </span>
            </div>
            <div style={{ display: "table-cell" }}>
              <span style={{ fontSize: "95%" }}>
                {formatDateWithTime(agent.last_dt)}
              </span>
            </div>
          </div>
        </td>
      </tr>
    );
  };
  if (!users) return <div>로딩 중...</div>;
  return (
    <div className="nk-content">
      <div className="container-fluid">
        <div className="nk-content-inner">
          <div className="nk-content-body">
            <div className="nk-block d-flex vPartnerLists">
              <div className="d-none d-xl-block w-30 me-1 mt-1 w-min-300px">
                <div className="dropdown partnerTree">
                  <div className="dropdown-menu p-3 ps-1 d-block">
                    {users && agents && (
                      <AgentTree
                        user={users}
                        onAgentClick={handleSelectAgent}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="w-100 card card-bordered">
                <div className="card-inner-group">
                  <div className="card-inner p-3">
                    <div className="nk-block nk-block-lg">
                      <div className="row tbSearch">
                        <div className="pb-1 w-10 me-1 mt-1 d-xs-block d-sm-block d-md-block d-lg-none d-xxl-none"></div>
                        {agents &&
                        agents.length > 0 &&
                        agents[0].user_class === "1" ? (
                          <>
                            <div className="col-12 col-sm-12 col-xl-4 col-xxl-1">
                              <div className="form-group d-grid d-xl-block">
                                <button
                                  className="btn btn-sm btn-primary text-center"
                                  onClick={() => openModal(selectAgent, 0)}
                                >
                                  <span>
                                    <i className="fas fa-plus-circle" />
                                    하부생성
                                  </span>
                                </button>
                              </div>
                            </div>

                            <div className="col-12 col-sm-12 col-xl-4 col-xxl-3">
                              <div
                                className={`custom-control switch-onoff custom-switch ms-2 ${
                                  showPendingOnly ? "checked" : ""
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  className="custom-control-input"
                                  id="sw-treeAllSelect"
                                  checked={showPendingOnly}
                                  onChange={handlePendingToggle}
                                />
                                <label
                                  className="custom-control-label text-nowrap"
                                  htmlFor="sw-treeAllSelect"
                                >
                                  <strong>승인대기</strong>
                                </label>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="col-12 col-sm-12 col-xl-4 col-xxl-4">
                              <div className="form-group d-grid d-xl-block">
                                <button
                                  className="btn btn-sm btn-primary text-center"
                                  onClick={() => openModal(selectAgent, 0)}
                                >
                                  <span>
                                    <i className="fas fa-plus-circle" />
                                    하부생성
                                  </span>
                                </button>
                              </div>
                            </div>
                          </>
                        )}

                        <div className="col-12 col-sm-12 col-xl-8 col-xxl-8">
                          <div className="form-control-wrap">
                            <div className="input-group">
                              <div className="input-group-prepend d-none d-sm-block">
                                <div className="btn btn-outline-primary btn-dim">
                                  <i className="fas fa-search" />
                                  <font>에이전트 아이디</font>
                                </div>
                              </div>
                              <input
                                aria-label="Text input with dropdown button"
                                className="form-control"
                                id="stext"
                                placeholder="에이전트 아이디"
                                type="text"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                              />
                              <select className="form-select">
                                <option value="">지갑</option>
                                <option value="y">심리스</option>
                              </select>
                              <div className="input-group-append">
                                <button
                                  className="btn btn-outline-primary btn-dim"
                                  onClick={() => fetchAgentInfo(users.myId)}
                                >
                                  <em className="icon ni ni-search me-2" /> 검색
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="card-inner p-0">
                    <PaginatedSortableControls
                      currentPage={currentPage}
                      totalPages={totalPages}
                      rowsPerPage={rowsPerPage}
                      rowsPerPageOptions={[10, 20, 50, 100]}
                      sortConfig={sortConfig}
                      sortOptions={sortOptions}
                      onPageChange={setCurrentPage}
                      onRowsPerPageChange={(v) => {
                        setRowsPerPage(v);
                        setCurrentPage(1);
                      }}
                      onSortChange={(field, order) => {
                        setSortConfig({ field, order });
                        setCurrentPage(1);
                      }}
                    />
                    <div
                      className="table-wrapper"
                      style={{
                        borderTop: "1px dotted #BBB",
                        paddingTop: "5px 0",
                      }}
                    >
                      <div className="btn-toolbar justify-content-between"></div>
                      <div
                        className="table-responsive"
                        data-pattern="priority-columns"
                      >
                        <div data-pattern="priority-columns">
                          <table
                            cellSpacing="0"
                            className="table table-small-font table-tighten table-bordered table-striped"
                          >
                            <thead>
                              <tr data-pos-y="0">
                                <th
                                  className="text-center"
                                  data-pos-x="0"
                                  data-priority="1"
                                  id="id5fa3e4edf1ca8-col-0"
                                  style={{
                                    display: "table-cell",
                                  }}
                                >
                                  트리정보
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="1"
                                  data-priority="1"
                                  id="id5fa3e4edf1ca8-col-1"
                                  style={{
                                    display: "table-cell",
                                  }}
                                >
                                  정보
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="2"
                                  data-priority="1"
                                  id="id5fa3e4edf1ca8-col-2"
                                  style={{
                                    display: "table-cell",
                                  }}
                                >
                                  결제
                                </th>
                                <th
                                  className="text-center col-wallet"
                                  data-bs-original-title="T : 트랜스, S : 심리스, M : 믹스형"
                                  data-bs-toggle="tooltip"
                                  data-pos-x="3"
                                  data-priority="1"
                                  id="id5fa3e4edf1ca8-col-3"
                                  style={{
                                    display: "table-cell",
                                  }}
                                >
                                  지갑{" "}
                                  <em className="icon ni ni-info-fill vam" />
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="4"
                                  data-priority="1"
                                  id="id5fa3e4edf1ca8-col-4"
                                  style={{
                                    display: "table-cell",
                                  }}
                                >
                                  요율
                                  <span
                                    data-bs-original-title="설정된 게임사 수"
                                    data-bs-toggle="tooltip"
                                    data-placement="top"
                                  >
                                    <em className="icon ni ni-info-fill vam" />
                                  </span>
                                </th>
                                <th
                                  className="text-center col-currency"
                                  data-pos-x="5"
                                  data-priority="1"
                                  id="id5fa3e4edf1ca8-col-5"
                                  style={{
                                    display: "table-cell",
                                  }}
                                >
                                  화폐
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="6"
                                  data-priority="1"
                                  id="id5fa3e4edf1ca8-col-6"
                                  style={{
                                    display: "table-cell",
                                  }}
                                >
                                  보유알
                                </th>
                                <th
                                  className="rowspan text-center"
                                  data-pos-x="7"
                                  data-priority="1"
                                  id="id5fa3e4edf1ca8-col-7"
                                  style={{
                                    display: "table-cell",
                                  }}
                                >
                                  <span
                                    data-bs-original-title="하부 에이전트의 총 보유알"
                                    data-bs-toggle="tooltip"
                                    data-placement="top"
                                  >
                                    하부보유알{" "}
                                    <em className="icon ni ni-info-fill vam" />
                                  </span>
                                  <hr />
                                  <span
                                    data-bs-original-title="회원들의 총 보유 머니"
                                    data-bs-toggle="tooltip"
                                    data-placement="top"
                                  >
                                    회원머니{" "}
                                    <em className="icon ni ni-info-fill vam" />
                                  </span>
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="8"
                                  data-priority="1"
                                  id="id5fa3e4edf1ca8-col-8"
                                  style={{
                                    display: "table-cell",
                                  }}
                                >
                                  <span
                                    data-bs-original-title="본인과 하부를 포함한 총 보유알"
                                    data-bs-toggle="tooltip"
                                    data-placement="top"
                                  >
                                    총 보유알{" "}
                                    <em className="icon ni ni-info-fill vam" />
                                  </span>
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="9"
                                  data-priority="1"
                                  id="id5fa3e4edf1ca8-col-9"
                                  style={{
                                    display: "table-cell",
                                  }}
                                >
                                  <span
                                    data-bs-original-title="본인이 상부로부터 지급 받거나 회수된 캐쉬"
                                    data-bs-toggle="tooltip"
                                    data-placement="top"
                                  >
                                    지급/회수{" "}
                                    <em className="icon ni ni-info-fill vam" />
                                  </span>
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="10"
                                  data-priority="1"
                                  id="id5fa3e4edf1ca8-col-10"
                                  style={{
                                    display: "table-cell",
                                  }}
                                >
                                  <span
                                    data-bs-original-title="본인이 하부,회원에게 지급하거나 회수한 캐쉬"
                                    data-bs-toggle="tooltip"
                                    data-placement="top"
                                  >
                                    하부 지급/회수{" "}
                                    <em className="icon ni ni-info-fill vam" />
                                  </span>{" "}
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="11"
                                  data-priority="1"
                                  id="id5fa3e4edf1ca8-col-11"
                                  style={{
                                    display: "table-cell",
                                  }}
                                >
                                  <span
                                    data-bs-original-title="본인이 회원에게 수동 지급하거나 회수한 캐쉬"
                                    data-bs-toggle="tooltip"
                                    data-placement="top"
                                  >
                                    회원 지급/회수{" "}
                                    <em className="icon ni ni-info-fill vam" />
                                  </span>{" "}
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="12"
                                  data-priority="1"
                                  id="id5fa3e4edf1ca8-col-12"
                                  style={{
                                    display: "table-cell",
                                  }}
                                >
                                  로그
                                </th>
                              </tr>
                            </thead>
                            {agents && agents_org && (
                              <PaginatedSortableTable
                                data={pagedAgents}
                                renderRow={renderRow}
                              />
                            )}
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showModal && selectAgent && (
        <MakeAgentModal
          agent={selectAgent}
          show={showModal}
          type={modalType}
          onClose={closeModal}
        />
      )}

      {showAModal && selectAgent && (
        <AmountModal
          agent={selectAgent}
          show={showAModal}
          type={modalType}
          onClose={closeModal2}
          topAgent={topAgent}
        />
      )}
    </div>
  );
}
